import React, { useRef, useState } from "react";
import { Upload, FileSpreadsheet, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

/**
 * ImportDropzone — Step 1.
 * Drag & drop area + file picker. Calls onFile(file) when a file is dropped/selected.
 * Shows a loading state while parent is analysing the file.
 */
export default function ImportDropzone({ onFile, loading = false }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const trigger = () => inputRef.current?.click();
  const handleInput = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile?.(f);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile?.(f);
  };

  return (
    <div className="space-y-4" data-testid="import-step-upload">
      <div
        role="button"
        tabIndex={loading ? -1 : 0}
        onClick={loading ? undefined : trigger}
        onKeyDown={(e) => { if (!loading && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); trigger(); } }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        aria-disabled={loading}
        className={cn(
          "w-full rounded-3xl border-2 border-dashed p-8 md:p-12 text-center transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          drag
            ? "border-primary bg-primary/5 scale-[1.005]"
            : "border-slate-300 dark:border-border bg-card dark:bg-surface hover:bg-slate-50 dark:hover:bg-surface/70 hover:border-slate-400 dark:hover:border-foreground/25",
          loading && "opacity-70 cursor-wait"
        )}
        data-testid="import-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".csv,.xlsx,.xls"
          onChange={handleInput}
          data-testid="import-file-input"
        />
        <div className="flex flex-col items-center gap-3">
          {loading ? (
            <div className="h-14 w-14 rounded-2xl bg-info/10 border border-info/30 flex items-center justify-center animate-pulse">
              <Loader2 className="h-6 w-6 text-info animate-spin" />
            </div>
          ) : (
            <div
              className={cn(
                "h-14 w-14 rounded-2xl border flex items-center justify-center transition-all duration-200",
                drag
                  ? "bg-primary/15 border-primary/40 text-primary"
                  : "bg-info/10 border-info/30 text-info"
              )}
            >
              <FileSpreadsheet className="h-6 w-6" />
            </div>
          )}
          <div className="space-y-1">
            <div className="text-base md:text-lg font-semibold tracking-tight">
              {loading ? tfr("Analyse de votre fichier…") : tfr("Déposer votre fichier ici")}
            </div>
            <div className="text-xs text-muted-foreground">
              {loading
                ? tfr("L'IA détecte automatiquement vos colonnes")
                : tfr("ou cliquez pour parcourir · CSV, XLSX jusqu'à 10 Mo")}
            </div>
          </div>
          {!loading && (
            <Button
              type="button"
              size="sm"
              onClick={(e) => { e.stopPropagation(); trigger(); }}
              className="mt-2 h-9 px-4 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
              data-testid="import-browse-btn"
            >
              <Upload className="h-4 w-4" /> {tfr("Parcourir les fichiers")}
            </Button>
          )}
        </div>
      </div>

      {/* Sub-info row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-start gap-3 rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 p-4">
          <div className="h-9 w-9 rounded-xl bg-info/10 border border-info/30 text-info flex items-center justify-center shrink-0">
            <FileSpreadsheet className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold">{tfr("Formats supportés")}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">CSV · XLSX (Excel)</div>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 p-4">
          <div className="h-9 w-9 rounded-xl bg-success/10 border border-success/30 text-success flex items-center justify-center shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold">{tfr("Sécurisé")}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{tfr("Vos données restent privées")}</div>
          </div>
        </div>
      </div>

      {/* AI hint */}
      <div className="flex items-start gap-3 rounded-2xl border border-info/20 bg-info/[0.04] p-4">
        <div className="h-9 w-9 rounded-xl bg-info/15 border border-info/30 text-info flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-foreground/90">{tfr("Importez plusieurs joueurs en une seule opération")}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {tfr("L'IA détecte et associe automatiquement vos colonnes au format Athlynx.")}
          </div>
        </div>
      </div>
    </div>
  );
}
